import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
    History,
    FileText,
    Package,
    ClipboardList,
    Activity,
    Info,
    ArrowRight
} from "lucide-react";

interface HistoryTabProps {
    installationId: number;
}

type BillingTimelineEvent = {
    id: string;
    type: "billing_audit" | "billing_notice";
    billingMilestoneId: number;
    milestoneLabel: string | null;
    milestoneType: string | null;
    action: string;
    label: string;
    createdAt: string | Date;
    userId: number | null;
    previousStatus: string | null;
    newStatus: string | null;
    reason: string | null;
    recipientEmail: string | null;
    recipientName: string | null;
    attempt: number | null;
    noticeStatus: string | null;
    emailLogId: number | null;
    errorMessage: string | null;
};

export function HistoryTab({ installationId }: HistoryTabProps) {
    const statusHistoryQuery = trpc.installationStatusHistory.list.useQuery({ installationId });
    const auditLogsQuery = trpc.auditLogs.list.useQuery({ installationId });
    const billingTimelineQuery = trpc.billingTimeline.list.useQuery({ installationId });
    const usersQuery = trpc.users.list.useQuery();

    const getUserName = (userId?: number | null) => {
        if (!userId) return "Sistema";
        return usersQuery.data?.find((u: { id: number; name?: string }) => u.id === userId)?.name || "Usuario";
    };

    const getStatusLabel = (status: string) => {
        const map: Record<string, string> = {
            pending: "Pendiente",
            in_progress: "Iniciada",
            completed: "Completada",
            cancelled: "Cancelada"
        };
        return map[status] || status;
    };

    const getStatusColor = (status: string) => {
        const map: Record<string, string> = {
            pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
            in_progress: "bg-blue-100 text-blue-800 border-blue-200",
            completed: "bg-green-100 text-green-800 border-green-200",
            cancelled: "bg-red-100 text-red-800 border-red-200",
            ready_to_invoice: "bg-blue-100 text-blue-800 border-blue-200",
            accounting_notified: "bg-purple-100 text-purple-800 border-purple-200",
            invoiced: "bg-green-100 text-green-800 border-green-200",
            sent_to_client: "bg-cyan-100 text-cyan-800 border-cyan-200",
            pending_collection: "bg-yellow-100 text-yellow-800 border-yellow-200",
            collected: "bg-emerald-100 text-emerald-800 border-emerald-200"
        };
        return map[status] || "bg-gray-100 text-gray-800 border-gray-200";
    };

    const getBillingStatusLabel = (status?: string | null) => {
        if (!status) return "—";
        const map: Record<string, string> = {
            pending: "Pendiente",
            ready_to_invoice: "Listo para factura",
            accounting_notified: "Notificado a contabilidad",
            invoiced: "Facturado",
            sent_to_client: "Enviado al cliente",
            pending_collection: "Cobro pendiente",
            collected: "Cobrado",
            cancelled: "Cancelado",
            sent: "Enviado",
            failed: "Fallido",
            superseded: "Invalidado por retroceso"
        };
        return map[status] || status;
    };

    const humanizeAuditAction = (action: string) => {
        const normalized = (action || "").trim();
        const map: Record<string, string> = {
            material_delivery_note_created: "Albarán creado",
            "Material Delivery Note Created": "Albarán creado",
            expense_created: "Gasto creado",
            "Expense Created": "Gasto creado",
            material_created: "Solicitud de material creada",
            material_batch_created: "Solicitud de material creada",
            "Material Request Created": "Solicitud de material creada",
            material_request_approved: "Solicitud de material aprobada",
            "Material Request Approved": "Solicitud de material aprobada",
            material_request_rejected: "Solicitud de material rechazada",
            "Material Request Rejected": "Solicitud de material rechazada",
            material_ordered_from_supplier: "Material pedido a proveedor",
            "Material Ordered": "Material pedido a proveedor",
            material_delivered_to_site: "Material recibido en obra",
            "Material Delivered To Site": "Material recibido en obra",
            material_ready_for_pickup: "Material listo para recoger",
            material_picked_up: "Material recogido por técnico",
            material_prepared_in_warehouse: "Material preparado en almacén",
            material_status_updated: "Estado de material actualizado",
            installation_updated: "Instalación actualizada",
            "Installation Updated": "Instalación actualizada",
            status_changed: "Estado actualizado",
            markSentToClient: "Envío al cliente registrado",
            "Status Changed": "Estado actualizado",
            expense_status_updated: "Estado de gasto actualizado",
        };
        return map[normalized] || map[normalized.replace(/_/g, " ")] || normalized.replace(/_/g, " ");
    };

    const humanizeAuditDetails = (details?: string) => {
        if (!details) return "";
        return details
            .replace(/Material Delivery Note Created/g, "Albarán creado")
            .replace(/Expense Created/g, "Gasto creado")
            .replace(/Material Request Created/g, "Solicitud de material creada")
            .replace(/Material Request Approved/g, "Solicitud de material aprobada")
            .replace(/Material Request Rejected/g, "Solicitud de material rechazada")
            .replace(/Material Ordered/g, "Material pedido a proveedor")
            .replace(/Material Delivered To Site/g, "Material recibido en obra")
            .replace(/Installation Updated/g, "Instalación actualizada")
            .replace(/Status Changed/g, "Estado actualizado")
            .replace(/created with (\d+) lines?/gi, "creado con $1 líneas")
            .replace(/Expense registered/g, "Gasto registrado")
            .replace(/expense_created/g, "Gasto creado")
            .replace(/material_delivery_note_created/g, "Albarán creado")
            .replace(/material_batch_created/g, "Solicitud de material creada")
            .replace(/material_request_approved/g, "Solicitud de material aprobada")
            .replace(/material_request_rejected/g, "Solicitud de material rechazada")
            .replace(/material_ordered_from_supplier/g, "Material pedido a proveedor")
            .replace(/material_delivered_to_site/g, "Material recibido en obra")
            .replace(/material_ready_for_pickup/g, "Material listo para recoger")
            .replace(/material_picked_up/g, "Material recogido por técnico")
            .replace(/material_prepared_in_warehouse/g, "Material preparado en almacén")
            .replace(/material_status_updated/g, "Estado de material actualizado")
            .replace(/expense_status_updated/g, "Estado de gasto actualizado");
    };

    const getActionIcon = (action: string) => {
        const normalized = (action || '').toLowerCase();
        if (normalized.includes('document')) return <FileText className="w-4 h-4 text-blue-500" />;
        if (normalized.includes('material')) return <Package className="w-4 h-4 text-orange-500" />;
        if (normalized.includes('report')) return <ClipboardList className="w-4 h-4 text-green-500" />;
        return <Info className="w-4 h-4 text-gray-500" />;
    };

    const renderBillingTimelineEvent = (event: BillingTimelineEvent) => (
        <div className="mt-1 bg-muted/20 p-3 rounded-lg border">
            <div className="flex items-center gap-2 font-medium mb-2">
                <FileText className="w-4 h-4 text-purple-500" />
                <span>{event.label}</span>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
                <div>
                    <span className="font-medium text-foreground">Hito:</span>{" "}
                    {event.milestoneLabel || `#${event.billingMilestoneId}`}
                </div>
                {(event.previousStatus || event.newStatus) && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {event.previousStatus && (
                            <Badge variant="outline" className={getStatusColor(event.previousStatus)}>
                                {getBillingStatusLabel(event.previousStatus)}
                            </Badge>
                        )}
                        {event.previousStatus && event.newStatus && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
                        {event.newStatus && (
                            <Badge variant="outline" className={getStatusColor(event.newStatus)}>
                                {getBillingStatusLabel(event.newStatus)}
                            </Badge>
                        )}
                    </div>
                )}
                {event.noticeStatus && (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-foreground">Estado aviso:</span>
                        <Badge variant="outline" className={getStatusColor(event.noticeStatus)}>
                            {getBillingStatusLabel(event.noticeStatus)}
                        </Badge>
                        {event.attempt != null && <span>Intento {event.attempt}</span>}
                    </div>
                )}
                {event.recipientEmail && (
                    <div className="break-words [overflow-wrap:anywhere]">
                        <span className="font-medium text-foreground">Destinatario:</span>{" "}
                        {event.recipientName || event.recipientEmail}
                        {event.recipientName ? ` (${event.recipientEmail})` : ""}
                    </div>
                )}
                {event.emailLogId != null && <div>emailLogId {event.emailLogId}</div>}
                {event.reason && (
                    <div className="break-words [overflow-wrap:anywhere]">
                        <span className="font-medium text-foreground">Motivo:</span> {event.reason}
                    </div>
                )}
                {event.errorMessage && (
                    <div className="text-red-600 break-words [overflow-wrap:anywhere]">
                        Error: {event.errorMessage}
                    </div>
                )}
            </div>
        </div>
    );

    // Merge and sort
    const historyItems = [
        ...(statusHistoryQuery.data?.map((item: { createdAt: string; userId: number }) => ({
            type: 'status',
            date: new Date(item.createdAt),
            userId: item.userId,
            data: item
        })) || []),
        ...(auditLogsQuery.data?.map((item: { createdAt: string; userId: number }) => ({
            type: 'audit',
            date: new Date(item.createdAt),
            userId: item.userId,
            data: item
        })) || []),
        ...((billingTimelineQuery.data as BillingTimelineEvent[] | undefined)?.map((item) => ({
            type: 'billing',
            date: new Date(item.createdAt),
            userId: item.userId,
            data: item
        })) || [])
    ].sort((a, b) => b.date.getTime() - a.date.getTime());

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Historial de Actividad
                </CardTitle>
                <CardDescription>Registro completo de eventos y cambios en la instalación</CardDescription>
            </CardHeader>
            <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                    <div className="space-y-6">
                        {statusHistoryQuery.isLoading || auditLogsQuery.isLoading || billingTimelineQuery.isLoading ? (
                            <p className="text-center text-muted-foreground">Cargando historial...</p>
                        ) : historyItems.length === 0 ? (
                            <p className="text-center text-muted-foreground">No hay actividad registrada.</p>
                        ) : (
                            historyItems.map((item, index) => (
                                <div key={index} className="relative pl-6 border-l-2 border-muted pb-6 last:pb-0">
                                    <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-background border-2 border-primary" />

                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground font-mono">
                                                {format(item.date, "d MMM yyyy, HH:mm", { locale: es })}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                Por: {getUserName(item.userId)}
                                            </span>
                                        </div>

                                        {item.type === 'status' ? (
                                            <div className="mt-1 bg-muted/20 p-3 rounded-lg border">
                                                <div className="flex items-center gap-2 font-medium mb-2">
                                                    <Activity className="w-4 h-4 text-primary" />
                                                    <span>Cambio de Estado</span>
                                                </div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Badge variant="outline" className={getStatusColor((item.data as any).previousStatus || 'pending')}>
                                                        {getStatusLabel((item.data as any).previousStatus || 'pending')}
                                                    </Badge>
                                                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                                    <Badge variant="outline" className={getStatusColor((item.data as any).newStatus)}>
                                                        {getStatusLabel((item.data as any).newStatus)}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ) : item.type === 'billing' ? (
                                            renderBillingTimelineEvent(item.data as BillingTimelineEvent)
                                        ) : (
                                            <div className="mt-1 bg-muted/20 p-3 rounded-lg border">
                                                <div className="flex items-center gap-2 font-medium mb-1">
                                                    {getActionIcon((item.data as any).action)}
                                                    <span>{humanizeAuditAction((item.data as any).action)}</span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    {humanizeAuditDetails((item.data as any).details)}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </ScrollArea>
            </CardContent>
        </Card>
    );
}
